import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { renderAsync } from 'docx-preview';
import { useI18n } from '../../../i18n';
import { useScaledHeight } from '../../../hooks/use-scaled-height';

interface DocxViewerProps {
    fileUrl: string;
    highlightQuotes?: Array<{
        quote: string;
        id: string;
    }>;
    scrollToQuote?: string;
    onScrollComplete?: () => void;
    exactMatch?: boolean;
}

// Normalize text for matching - collapse whitespace and handle special characters
const normalizeText = (text: string): string => {
    return text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[""]/g, '"')
        .replace(/['']/g, "'")
        .replace(/[–-]/g, '-')
        .trim()
        .toLowerCase();
};

// Extract significant words from text (for fuzzy matching)
const getSignificantWords = (text: string): string[] => {
    return normalizeText(text)
        .split(/\s+/)
        .filter(word => word.length > 3)
        .slice(0, 10);
};

const normalizeChar = (char: string): string => {
    if (/\s/.test(char)) return ' ';
    if (char === '\u201C' || char === '\u201D') return '"';
    if (char === '\u2018' || char === '\u2019') return "'";
    if (char === '\u2013' || char === '\u2014') return '-';
    return char.toLowerCase();
};

const highlightExactQuote = (container: HTMLElement, quote: string, id: string): Element | null => {
    const target = Array.from(quote.replace(/\*\*/g, ''))
        .map(normalizeChar)
        .join('')
        .replace(/ +/g, ' ')
        .trim();
    if (!target) return null;
    let first: Element | null = null;

    container.querySelectorAll('p').forEach((paragraph) => {
        const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT, null);
        const positions: Array<{ node: Text; offset: number }> = [];
        let text = '';
        let node: Node | null;
        while ((node = walker.nextNode())) {
            const value = node.textContent || '';
            for (let i = 0; i < value.length; i++) {
                const char = normalizeChar(value[i]);
                if (char === ' ' && (text === '' || text.endsWith(' '))) continue;
                text += char;
                positions.push({ node: node as Text, offset: i });
            }
        }

        const ranges: Array<{ start: number; end: number }> = [];
        for (let from = text.indexOf(target); from !== -1; from = text.indexOf(target, from + target.length)) {
            ranges.push({ start: from, end: from + target.length - 1 });
        }

        let paragraphFirst: Element | null = null;
        for (const { start, end } of ranges.reverse()) {
            const pieces = new Map<Text, { start: number; end: number }>();
            for (let i = start; i <= end; i++) {
                const { node: textNode, offset } = positions[i];
                const piece = pieces.get(textNode);
                pieces.set(textNode, piece ? { start: piece.start, end: offset + 1 } : { start: offset, end: offset + 1 });
            }
            for (const [textNode, piece] of Array.from(pieces.entries()).reverse()) {
                const range = document.createRange();
                range.setStart(textNode, piece.start);
                range.setEnd(textNode, piece.end);
                const span = document.createElement('span');
                span.className = 'source-highlight';
                span.setAttribute('data-source-id', id);
                span.setAttribute('data-quote', quote);
                range.surroundContents(span);
                paragraphFirst = span;
            }
        }
        if (!first) first = paragraphFirst;
    });

    return first;
};

// A4 page width in px at 96dpi (210mm = 793.7px, but docx-preview uses ~816px with margins)
const DOCX_PAGE_WIDTH = 816;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;

export const DocxViewer: React.FC<DocxViewerProps> = ({ fileUrl, highlightQuotes = [], scrollToQuote, onScrollComplete, exactMatch = false }) => {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rendered, setRendered] = useState(false);
    const [zoom, setZoom] = useState(0.6);
    const [autoZoom, setAutoZoom] = useState(0.6);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const styleRef = useRef<HTMLDivElement>(null);
    const lastScrollQuoteRef = useRef<string | null>(null);
    const lastHighlightKeyRef = useRef<string>('');
    const scaledHeight = useScaledHeight(containerRef, zoom);

    // Calculate fit-to-width zoom based on container size
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const containerWidth = entry.contentRect.width;
                // Subtract padding (16px each side) for the page wrapper
                const fitZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX,
                    (containerWidth - 32) / DOCX_PAGE_WIDTH
                ));
                const rounded = Math.round(fitZoom * 20) / 20; // round to nearest 0.05
                setAutoZoom(rounded);
                setZoom(rounded);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const zoomIn = useCallback(() => setZoom(z => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 10) / 10)), []);
    const zoomOut = useCallback(() => setZoom(z => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 10) / 10)), []);
    const zoomFit = useCallback(() => setZoom(autoZoom), [autoZoom]);
    const zoomLabel = useMemo(() => `${Math.round(zoom * 100)}%`, [zoom]);

    useEffect(() => {
        const loadDocument = async () => {
            if (!containerRef.current) return;

            try {
                setLoading(true);
                setError(null);
                setRendered(false);

                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error('Failed to fetch document');
                }

                const blob = await response.blob();

                // Clear previous content
                containerRef.current.innerHTML = '';

                await renderAsync(blob, containerRef.current, styleRef.current ?? undefined, {
                    className: 'docx-preview',
                    inWrapper: true,
                    ignoreWidth: false,
                    ignoreHeight: false,
                    ignoreFonts: false,
                    breakPages: true,
                    renderHeaders: true,
                    renderFooters: true,
                    renderFootnotes: true,
                    renderEndnotes: true,
                    renderComments: false,
                    renderChanges: false,
                });

                setRendered(true);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load document');
            } finally {
                setLoading(false);
            }
        };

        loadDocument();
    }, [fileUrl]);

    // Highlight a quote in the document
    const highlightQuote = useCallback((container: HTMLElement, quote: string, id: string): Element | null => {
        const normalizedQuote = normalizeText(quote);
        const quoteWords = getSignificantWords(quote);

        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
        const textNodes: Text[] = [];
        let node: Node | null;
        while ((node = walker.nextNode())) {
            textNodes.push(node as Text);
        }

        let highlightedElement: Element | null = null;

        for (const textNode of textNodes) {
            const nodeText = textNode.textContent || '';
            const normalizedNodeText = normalizeText(nodeText);
            const parent = textNode.parentNode;

            if (!parent || (parent as Element).closest?.('.source-highlight')) {
                continue;
            }

            let shouldHighlight = false;

            if (normalizedNodeText.includes(normalizedQuote) || normalizedQuote.includes(normalizedNodeText)) {
                shouldHighlight = normalizedNodeText.length > 0;
            }

            if (!shouldHighlight && quoteWords.length > 0) {
                const matchingWords = quoteWords.filter(word => normalizedNodeText.includes(word));
                shouldHighlight = matchingWords.length >= Math.min(2, quoteWords.length);
            }

            if (!shouldHighlight && normalizedQuote.length > 10) {
                const firstWords = normalizedQuote.substring(0, 30);
                const lastWords = normalizedQuote.substring(Math.max(0, normalizedQuote.length - 30));
                shouldHighlight = normalizedNodeText.includes(firstWords) || normalizedNodeText.includes(lastWords);
            }

            if (shouldHighlight) {
                const span = document.createElement('span');
                span.className = 'source-highlight';
                span.setAttribute('data-source-id', id);
                span.setAttribute('data-quote', quote);
                span.textContent = nodeText;

                try {
                    parent.replaceChild(span, textNode);
                    if (!highlightedElement) {
                        highlightedElement = span;
                    }
                } catch (e) {
                    // Node might have been modified, skip
                }
            }
        }

        return highlightedElement;
    }, []);

    // Apply highlights after render
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !rendered || loading) return;

        const highlightKey = highlightQuotes.map(h => h.quote).join('|||');
        if (highlightKey === lastHighlightKeyRef.current) return;
        lastHighlightKeyRef.current = highlightKey;

        // Remove existing highlights
        container.querySelectorAll('.source-highlight').forEach(el => {
            const parent = el.parentNode;
            if (parent) {
                const text = document.createTextNode(el.textContent || '');
                parent.replaceChild(text, el);
                parent.normalize();
            }
        });

        if (highlightQuotes.length === 0) return;

        let firstHighlight: Element | null = null;
        for (const { quote, id } of highlightQuotes) {
            if (!quote || !quote.trim()) continue;
            const highlighted = exactMatch
                ? highlightExactQuote(container, quote, id)
                : highlightQuote(container, quote, id);
            if (highlighted && !firstHighlight) {
                firstHighlight = highlighted;
            }
        }
    }, [rendered, highlightQuotes, loading, highlightQuote, exactMatch]);

    // Handle scrolling
    useEffect(() => {
        if (!scrollToQuote || !rendered || scrollToQuote === lastScrollQuoteRef.current) return;

        const container = containerRef.current;
        if (!container) {
            onScrollComplete?.();
            return;
        }

        lastScrollQuoteRef.current = scrollToQuote;

        setTimeout(() => {
            const normalizedScrollQuote = normalizeText(scrollToQuote);
            const highlights = container.querySelectorAll('.source-highlight');
            let targetElement: Element | null = null;

            for (const highlight of highlights) {
                const hQuote = highlight.getAttribute('data-quote') || '';
                const normalizedHQuote = normalizeText(hQuote);

                if (normalizedHQuote === normalizedScrollQuote ||
                    normalizedHQuote.includes(normalizedScrollQuote) ||
                    normalizedScrollQuote.includes(normalizedHQuote)) {
                    targetElement = highlight;
                    break;
                }
            }

            if (!targetElement && highlights.length > 0) {
                targetElement = highlights[0];
            }

            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                targetElement.classList.add('scroll-flash');
                setTimeout(() => targetElement?.classList.remove('scroll-flash'), 1500);
            }

            onScrollComplete?.();
        }, 150);
    }, [scrollToQuote, onScrollComplete, rendered]);

    useEffect(() => {
        if (!scrollToQuote) {
            lastScrollQuoteRef.current = null;
        }
    }, [scrollToQuote]);

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">{t.playbooks.viewer.error(error)}</div>
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Hidden container for docx-preview injected styles */}
            <div ref={styleRef} style={{ display: 'none' }} />

            {/* Zoom controls */}
            {!loading && rendered && (
                <div className="flex items-center justify-center gap-1 py-1 border-b bg-background flex-shrink-0">
                    <button onClick={zoomOut} className="p-1 rounded hover:bg-muted text-muted-foreground" title={t.playbooks.viewer.zoomOut}>
                        <ZoomOut className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={zoomFit} className="px-1 py-0.5 rounded hover:bg-muted" title={t.playbooks.viewer.fitToWidth}>
                        <span className="text-xs text-muted-foreground w-10 text-center select-none">{zoomLabel}</span>
                    </button>
                    <button onClick={zoomIn} className="p-1 rounded hover:bg-muted text-muted-foreground" title={t.playbooks.viewer.zoomIn}>
                        <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}

            <div className="flex-1 min-h-0 relative">
                <div ref={scrollContainerRef} className="absolute inset-0 overflow-auto">
                    {loading && (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-gray-500">{t.playbooks.viewer.loadingDocument}</div>
                        </div>
                    )}
                    {/* Sized to the scaled content so scrolling ends at the last page */}
                    <div style={{ height: scaledHeight, overflow: 'hidden' }}>
                        <div
                            ref={containerRef}
                            id="docx-content"
                            className="docx-viewer-container"
                            style={{
                                display: loading ? 'none' : undefined,
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top center',
                            }}
                        />
                    </div>
                </div>
            </div>
            <style>{`
                .docx-viewer-container .docx-preview-wrapper {
                    background: hsl(var(--muted) / 0.3);
                    padding: 16px;
                }
                .docx-viewer-container .docx-preview-wrapper section.docx {
                    background: white;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.1);
                    margin-bottom: 16px;
                    border-radius: 2px;
                }
                .source-highlight {
                    background-color: #fef08a;
                    padding: 2px 0;
                    border-radius: 2px;
                    transition: background-color 0.2s;
                }
                .source-highlight:hover {
                    background-color: #fde047;
                }
                .source-highlight.scroll-flash {
                    animation: flash 1.5s ease-out;
                }
                @keyframes flash {
                    0%, 50% { background-color: #f97316; }
                    100% { background-color: #fef08a; }
                }
            `}</style>
        </div>
    );
};
